/**
 * WelcomeTermsStep - Institutional Grade Terms Acceptance
 *
 * Step 0 of QBridge Setup Wizard
 * - Welcome message with value proposition
 * - Legal disclaimer (NOT a broker, NOT a signal provider)
 * - Terms & Privacy Policy acceptance checkbox
 * - Continue button disabled until checkbox is checked
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Link
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const WelcomeTermsStep = ({ onAccept, colors }) => {
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Value propositions
    const features = [
        {
            icon: <SyncIcon sx={{ fontSize: '1.25rem' }} />,
            title: 'Automatic Trade Sync',
            description: 'Your MT5 trades sync in real-time to Q AI Coach'
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: '1.25rem' }} />,
            title: 'Personalized Insights',
            description: 'AI analysis tailored to YOUR trading patterns'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: '1.25rem' }} />,
            title: 'Secure & Private',
            description: 'Data processed locally, bank-grade encryption'
        },
        {
            icon: <SpeedIcon sx={{ fontSize: '1.25rem' }} />,
            title: 'Background Operation',
            description: 'Runs silently in system tray, zero interference'
        }
    ];

    const handleTermsChange = (event) => {
        setTermsAccepted(event.target.checked);
    };

    const handleContinue = () => {
        if (termsAccepted) {
            onAccept();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                py: 2
            }}
        >
            {/* ═══════════════════════════════════════════════════════════════════
                LOGO & WELCOME
                ═══════════════════════════════════════════════════════════════════ */}
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                {/* Animated Logo */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '20px',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: `0 0 40px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.4)'}`,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%, 100%': {
                                boxShadow: `0 0 20px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.3)'}`
                            },
                            '50%': {
                                boxShadow: `0 0 40px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.5)'}`
                            }
                        }
                    }}
                >
                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontSize: '2.5rem',
                            fontWeight: 800,
                            fontFamily: "'Inter', sans-serif"
                        }}
                    >
                        Q
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        mb: 0.5
                    }}
                >
                    Welcome to QBridge
                </Typography>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '1rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    Your AI Trading Coach Awaits
                </Typography>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                VALUE PROPOSITIONS
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '560px',
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    p: 2.5
                }}
            >
                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        mb: 2,
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: 1.4
                    }}
                >
                    What You'll Get
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {features.map((feature, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5
                            }}
                        >
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    background: colors.primaryBg || 'rgba(39, 181, 109, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: colors.primary,
                                    flexShrink: 0
                                }}
                            >
                                {feature.icon}
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: colors.text,
                                        fontSize: '0.9375rem',
                                        fontWeight: 600,
                                        fontFamily: "'Inter', sans-serif",
                                        lineHeight: 1.3
                                    }}
                                >
                                    {feature.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.textMuted,
                                        fontSize: '0.875rem',
                                        fontFamily: "'Inter', sans-serif",
                                        lineHeight: 1.5
                                    }}
                                >
                                    {feature.description}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                LEGAL DISCLAIMER
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '560px',
                    background: colors.warningBg || 'rgba(255, 165, 0, 0.08)',
                    border: `1px solid ${colors.warning || '#FFA500'}30`,
                    borderRadius: '12px',
                    p: 2.5
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ color: colors.warning || '#FFA500', fontSize: '1.25rem' }} />
                    <Typography
                        sx={{
                            color: colors.warning || '#FFA500',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif"
                        }}
                    >
                        IMPORTANT NOTICE
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.9375rem',
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: 1.6,
                        mb: 1.5
                    }}
                >
                    Q AI Coach is an <strong style={{ color: colors.text }}>EDUCATIONAL SOFTWARE TOOL</strong>.
                </Typography>

                <Box sx={{ mb: 1.5 }}>
                    <Typography
                        sx={{
                            color: colors.textMuted,
                            fontSize: '0.9375rem',
                            fontFamily: "'Inter', sans-serif",
                            mb: 0.75,
                            lineHeight: 1.5
                        }}
                    >
                        We are NOT:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                        {[
                            'A broker, dealer, or financial institution',
                            'A signal provider or trading advisor',
                            'Providing investment advice or recommendations'
                        ].map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ color: colors.error || '#EF4444', fontSize: '0.875rem' }}>✗</Typography>
                                <Typography
                                    sx={{
                                        color: colors.textMuted,
                                        fontSize: '0.875rem',
                                        fontFamily: "'Inter', sans-serif",
                                        lineHeight: 1.5
                                    }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.875rem',
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: 1.6,
                        fontStyle: 'italic'
                    }}
                >
                    Trading foreign exchange carries a HIGH LEVEL OF RISK. Past performance is NOT indicative of future results.
                    You should carefully consider your investment objectives and risk appetite.
                </Typography>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                TERMS ACCEPTANCE
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '560px',
                    background: colors.surface,
                    border: `1px solid ${termsAccepted ? colors.primaryBorder : colors.border}`,
                    borderRadius: '12px',
                    p: 2.5,
                    transition: 'border-color 0.2s ease'
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAccepted}
                            onChange={handleTermsChange}
                            sx={{
                                color: colors.textMuted,
                                '&.Mui-checked': {
                                    color: colors.primary
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1.5rem'
                                }
                            }}
                        />
                    }
                    label={
                        <Typography
                            sx={{
                                color: colors.textSecondary,
                                fontSize: '0.875rem',
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            I have read and accept the{' '}
                            <Link
                                href="https://www.qforgex.com/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: colors.primary,
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Terms of Service
                            </Link>{' '}
                            and{' '}
                            <Link
                                href="https://www.qforgex.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: colors.primary,
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Privacy Policy
                            </Link>
                        </Typography>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                />

                {/* Quick Links */}
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pl: 4 }}>
                    <Link
                        href="https://www.qforgex.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: colors.textMuted,
                            fontSize: '0.875rem',
                            textDecoration: 'none',
                            fontFamily: "'Inter', sans-serif",
                            '&:hover': { color: colors.primary }
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                        View Terms of Service
                    </Link>
                    <Link
                        href="https://www.qforgex.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: colors.textMuted,
                            fontSize: '0.875rem',
                            textDecoration: 'none',
                            fontFamily: "'Inter', sans-serif",
                            '&:hover': { color: colors.primary }
                        }}
                    >
                        <SecurityIcon sx={{ fontSize: '1rem' }} />
                        View Privacy Policy
                    </Link>
                </Box>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                CONTINUE BUTTON
                ═══════════════════════════════════════════════════════════════════ */}
            <Box sx={{ width: '100%', maxWidth: '560px' }}>
                <Button
                    fullWidth
                    onClick={handleContinue}
                    disabled={!termsAccepted}
                    sx={{
                        py: 1.75,
                        px: 4,
                        borderRadius: '10px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'none',
                        background: termsAccepted
                            ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`
                            : colors.surface,
                        color: termsAccepted ? '#FFFFFF' : colors.textDim,
                        border: termsAccepted ? 'none' : `1px solid ${colors.border}`,
                        boxShadow: termsAccepted ? `0 4px 20px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.3)'}` : 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: termsAccepted
                                ? `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`
                                : colors.surface,
                            boxShadow: termsAccepted ? `0 6px 24px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.4)'}` : 'none'
                        },
                        '&.Mui-disabled': {
                            background: colors.surface,
                            color: colors.textDim,
                            border: `1px solid ${colors.border}`
                        }
                    }}
                    endIcon={<ArrowForwardIcon />}
                >
                    Get Started
                </Button>

                {!termsAccepted && (
                    <Typography
                        sx={{
                            color: colors.textDim,
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            mt: 1,
                            fontFamily: "'Inter', sans-serif",
                            lineHeight: 1.5
                        }}
                    >
                        Please accept the terms to continue
                    </Typography>
                )}
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                SECURITY BADGE
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1
                }}
            >
                <SecurityIcon sx={{ color: colors.primary, fontSize: '1rem' }} />
                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.75rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    Your data is protected with AES-256 encryption
                </Typography>
            </Box>
        </Box>
    );
};

export default WelcomeTermsStep;
